import axios from 'axios';

/**
 * Mengambil token autentikasi JWT dari berbagai kemungkinan lokasi penyimpanan
 */
export function getAuthToken() {
    try {
        const candidate = (
            localStorage.getItem('jwt_token') ||
            sessionStorage.getItem('jwt_token') ||
            localStorage.getItem('token') ||
            sessionStorage.getItem('token') ||
            (() => {
                try {
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    return user.token || user.jwt_token || null;
                } catch {
                    return null;
                }
            })() ||
            (() => {
                try {
                    const tokenData = JSON.parse(localStorage.getItem('token_data') || '{}');
                    return tokenData.token || tokenData.jwt_token || null;
                } catch {
                    return null;
                }
            })()
        );

        if (!candidate || typeof candidate !== 'string' || candidate === 'null' || candidate === 'undefined') {
            return null;
        }

        const trimmed = candidate.trim().replace(/^["']|["']$/g, '');
        if (trimmed.startsWith('ey') && trimmed.split('.').length === 3) {
            return trimmed;
        } else if (trimmed.startsWith('Bearer ey')) {
            return trimmed.replace(/^Bearer\s+/i, '');
        }
        return trimmed;
    } catch {
        return null;
    }
}

/**
 * Memeriksa apakah token JWT sudah kedaluwarsa (expired)
 * berdasarkan claim 'exp' pada payload JWT, dengan kompensasi clock skew (selisih jam user & server).
 */
export function isTokenExpired(token, bufferSeconds = 0) {
    if (!token || typeof token !== 'string') return true;
    try {
        const clean = token.trim().replace(/^["']|["']$/g, '');
        const parts = clean.split('.');
        if (parts.length !== 3) return true;

        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padLength = (4 - (base64.length % 4)) % 4;
        const paddedBase64 = base64 + '='.repeat(padLength);

        let jsonPayload;
        try {
            const rawDecoded = atob(paddedBase64);
            jsonPayload = decodeURIComponent(
                rawDecoded
                    .split('')
                    .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
        } catch {
            jsonPayload = atob(paddedBase64);
        }

        const payload = JSON.parse(jsonPayload);
        if (!payload || typeof payload.exp === 'undefined') {
            return false;
        }

        const nowSec = Math.floor(Date.now() / 1000);
        return (nowSec + bufferSeconds) >= Number(payload.exp);
    } catch {
        return true;
    }
}

/**
 * Menghapus semua cookies yang dapat diakses oleh browser
 */
export function clearAllCookies() {
    try {
        if (typeof document === 'undefined' || !document.cookie) return;

        const cookies = document.cookie.split(';');
        const hostname = window.location.hostname;
        const pathSegments = window.location.pathname.split('/');

        const domains = ['', hostname];
        const domainParts = hostname.split('.');
        if (domainParts.length > 1) {
            domains.push('.' + hostname);
            if (domainParts.length > 2) {
                domains.push('.' + domainParts.slice(-2).join('.'));
            }
        }

        const paths = ['/', ''];
        let currentPath = '';
        pathSegments.forEach((segment) => {
            if (segment) {
                currentPath += '/' + segment;
                paths.push(currentPath);
                paths.push(currentPath + '/');
            }
        });

        cookies.forEach((cookie) => {
            const eqPos = cookie.indexOf('=');
            const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
            if (name) {
                domains.forEach((d) => {
                    paths.forEach((p) => {
                        const domainStr = d ? `;domain=${d}` : '';
                        const pathStr = p ? `;path=${p}` : ';path=/';
                        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT${pathStr}${domainStr}`;
                    });
                });
            }
        });
    } catch (err) {
        console.warn('Gagal membersihkan cookies:', err);
    }
}

/**
 * Membersihkan semua data sesi di localStorage, sessionStorage, dan cookies
 */
export function clearAllAuthStorage() {
    try {
        localStorage.clear();
    } catch (e) {
        console.warn('Gagal membersihkan localStorage:', e);
    }
    try {
        sessionStorage.clear();
    } catch (e) {
        console.warn('Gagal membersihkan sessionStorage:', e);
    }
    clearAllCookies();
    if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
        delete axios.defaults.headers.common['Authorization'];
    }
}

/**
 * Membersihkan semua token dari storage saat sesi expired
 */
export function clearAuthToken() {
    clearAllAuthStorage();
}

/**
 * Validasi sesi autentikasi di awal (bootstrap):
 * Memeriksa apakah token JWT atau sesi localStorage masih valid sebelum masuk dashboard.
 * Jika rute terproteksi diakses dengan sesi kadaluarsa atau tanpa sesi aktif,
 * arahkan ke /loadingout sebelum halaman di-render.
 */
export function validateInitialAuthSession() {
    if (typeof window === 'undefined') return { isValid: false, expired: false };

    // Bersihkan offset waktu server lawas jika ada di storage
    try {
        localStorage.removeItem('server_time_offset');
        sessionStorage.removeItem('server_time_offset');
    } catch (e) {}

    const pathname = (window.location.pathname || '').toLowerCase();
    const publicRoutes = ['/', '/loadingout', '/privacy', '/privacy-policy'];
    const isPublicRoute = publicRoutes.some((route) => pathname === route || pathname === route + '/');

    // Sinkronisasi session storage legacy jika ada di localStorage
    if (localStorage.getItem('adm') && !sessionStorage.getItem('adm')) {
        sessionStorage.setItem('adm', localStorage.getItem('adm'));
    }
    if (localStorage.getItem('un') && !localStorage.getItem('logus')) {
        localStorage.setItem('logus', localStorage.getItem('un'));
    }

    const authId = localStorage.getItem('un') || localStorage.getItem('adm') || sessionStorage.getItem('un');
    const jwtToken = getAuthToken();
    const isExpired = jwtToken ? isTokenExpired(jwtToken, 60) : true;
    const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

    if (jwtToken && isExpired && !refreshToken) {
        clearAllAuthStorage();
        if (!isPublicRoute) {
            window.location.replace('/loadingout');
        }
        return { isValid: false, expired: true };
    }

    if (!isPublicRoute && !authId && !refreshToken) {
        clearAllAuthStorage();
        window.location.replace('/loadingout');
        return { isValid: false, expired: false };
    }

    if ((jwtToken || refreshToken) && authId) {
        if (refreshToken && isExpired) {
            console.log('[JWT FO] Bootstrap auto-refreshing expired token...');
            getOrRefreshAccessToken('front-office').catch((e) => {
                console.warn('[Initial Auto-Refresh Failed (FO)]:', e);
            });
        }
        return { isValid: true, expired: false };
    }

    return { isValid: false, expired: false };
}

/**
 * Helper untuk menyimpan sesi token ke seluruh storage dan menginjeksi default Axios
 */
export function setAuthSession(data) {
    if (!data) return;
    const token = typeof data === 'string' ? data : (data.token || data.jwt_token);
    if (!token || token === 'null' || token === 'undefined') return;

    const tokenType = (data && data.token_type) ? data.token_type : 'Bearer';

    try {
        localStorage.setItem('jwt_token', token);
        localStorage.setItem('token', token);
        localStorage.setItem('token_type', tokenType);

        if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
        }

        if (data.role) localStorage.setItem('user_role', data.role);
        if (data.tipe) localStorage.setItem('user_tipe', data.tipe);
        if (data.user_id) localStorage.setItem('user_id', data.user_id);
        if (data.user_name) localStorage.setItem('user_name', data.user_name);
    } catch (e) {
        console.warn('Gagal menyimpan token ke localStorage:', e);
    }

    // Salin juga ke sessionStorage untuk redundansi
    try {
        sessionStorage.setItem('jwt_token', token);
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('token_type', tokenType);
        if (data.refresh_token) {
            sessionStorage.setItem('refresh_token', data.refresh_token);
        }
    } catch (e) {}

    if (axios && axios.defaults && axios.defaults.headers) {
        axios.defaults.headers.common = axios.defaults.headers.common || {};
        axios.defaults.headers.common['Authorization'] = `${tokenType} ${token}`;
    }
}

/**
 * Cek apakah URL target merupakan API internal/sistem yang wajib menerima token
 */
function isInternalUrl(url) {
    if (!url || typeof url !== 'string') return true;
    if (!/^https?:\/\//i.test(url) && !url.startsWith('//')) return true;

    const lower = url.toLowerCase();
    return (
        lower.includes('.asia.ac.id') ||
        lower.includes('localhost') ||
        lower.includes('127.0.0.1') ||
        lower.includes('192.168.') ||
        lower.includes('202.124.125.') ||
        (typeof window !== 'undefined' && window.location && window.location.hostname && lower.includes(window.location.hostname.toLowerCase()))
    );
}

/**
 * Status antrean refresh token untuk mencegah race condition (multiple concurrent refresh requests)
 */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

let proactiveRefreshPromise = null;

/**
 * Memperbarui access token ke apiastor secara aman menggunakan antrean tunggal (shared promise).
 * Mencegah duplikasi request refresh saat banyak request keluar bersamaan ke berbagai microservice.
 */
export async function getOrRefreshAccessToken(appType = 'front-office', customRefreshUrl = null) {
    const token = getAuthToken();
    const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

    // Jika token masih aman (> 60 detik sebelum exp) atau tidak ada refresh token, return token saat ini
    if (token && !isTokenExpired(token, 60)) {
        return token;
    }

    if (!refreshToken) {
        return token;
    }

    if (proactiveRefreshPromise) {
        return proactiveRefreshPromise;
    }

    proactiveRefreshPromise = (async () => {
        try {
            const defaultBaseAstor = 'https://apiastor.asia.ac.id/public/';
            const baseAstor = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_ASTOR)
                ? String(process.env.REACT_APP_API_ASTOR).trim()
                : defaultBaseAstor;

            let refreshUrl = customRefreshUrl;
            if (!refreshUrl) {
                refreshUrl = baseAstor.endsWith('/') ? `${baseAstor}auth/refresh` : `${baseAstor}/auth/refresh`;
            }

            const payloadBody = { refresh_token: refreshToken };
            if (appType) {
                payloadBody.app_type = appType;
            }

            console.log('[JWT FO] Fetching refreshed access token from:', refreshUrl);

            const refreshResponse = await axios.post(
                refreshUrl,
                payloadBody,
                { headers: { 'Content-Type': 'application/json', 'Authorization': '' } }
            );

            if (refreshResponse && refreshResponse.data && refreshResponse.data.token) {
                const newAccessToken = refreshResponse.data.token;
                setAuthSession(refreshResponse.data);
                return newAccessToken;
            } else {
                throw new Error('Respons refresh token tidak valid.');
            }
        } catch (error) {
            console.error('Gagal memperbarui token ke IdP:', error);
            if (error && error.response && error.response.status === 401) {
                clearAllAuthStorage();
                window.location.replace('/loadingout');
            }
            throw error;
        } finally {
            proactiveRefreshPromise = null;
        }
    })();

    return proactiveRefreshPromise;
}

/**
 * JWT Request and Response Interceptor
 * Menginjeksi Authorization Bearer token otomatis pada setiap outgoing request,
 * memvalidasi expiration sebelum kirim, dan menangani respons 401 (Auto-Refresh Silent) & 403
 */
export function attachJwtInterceptor(axiosInstance) {
    if (!axiosInstance || !axiosInstance.interceptors) {
        return axiosInstance;
    }

    if (axiosInstance.__jwtInterceptorAttached) {
        return axiosInstance;
    }
    axiosInstance.__jwtInterceptorAttached = true;

    // Pasang token awal jika sudah tersedia
    const currentToken = getAuthToken();
    if (currentToken && axiosInstance.defaults && axiosInstance.defaults.headers) {
        axiosInstance.defaults.headers.common = axiosInstance.defaults.headers.common || {};
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
    }

    // Request Interceptor: Proactive Refresh + Lampirkan header Authorization
    axiosInstance.interceptors.request.use(
        async (config) => {
            if (config.baseURL && typeof config.url === 'string' && config.url.startsWith('/') && !config.url.startsWith('//')) {
                config.url = config.url.replace(/^\/+/, '');
            }

            const isAuthEndpoint = typeof config.url === 'string' && (
                config.url.includes('login/simaka/fo/ul') ||
                config.url.includes('simaka/login/search/inview') ||
                config.url.includes('mencari/aks/fo/log') ||
                config.url.includes('/login') ||
                config.url.includes('user/login') ||
                config.url.includes('/auth/refresh') ||
                config.url.includes('/auth/logout')
            );

            if (isAuthEndpoint && config.headers) {
                delete config.headers['Authorization'];
                delete config.headers['authorization'];
                if (config.headers.common) {
                    delete config.headers.common['Authorization'];
                    delete config.headers.common['authorization'];
                }
            }

            // Pilar 1 (Proactive Refresh): Pastikan token selalu aktif sebelum request dikirim ke service manapun
            let token = getAuthToken();
            const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

            if (!isAuthEndpoint && refreshToken && (!token || isTokenExpired(token, 60))) {
                try {
                    token = await getOrRefreshAccessToken('front-office');
                } catch (e) {
                    // Jika refresh gagal, biarkan request berlanjut agar ditangani fallback jika perlu
                }
            }

            const targetUrl = config.url || '';
            const shouldAttach = isInternalUrl(targetUrl);

            if (token && shouldAttach && !isAuthEndpoint) {
                config.headers = config.headers || {};
                const tokenType = localStorage.getItem('token_type') || sessionStorage.getItem('token_type') || 'Bearer';
                const authValue = token.startsWith('Bearer ') ? token : `${tokenType} ${token}`;
                config.headers['Authorization'] = authValue;
                if (config.headers.common) {
                    config.headers.common['Authorization'] = authValue;
                }
                if (typeof config.headers.set === 'function') {
                    config.headers.set('Authorization', authValue);
                }
                if (axios && axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
                    axios.defaults.headers.common['Authorization'] = authValue;
                }
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response Interceptor: Tangani 401 (Auto Refresh Fallback Safety Net) & 403 (Forbidden)
    axiosInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config;

            if (error.response) {
                const status = error.response.status;

                // Tangani 401: Coba perbarui token menggunakan Refresh Token secara silent (Fallback)
                if (status === 401 && originalRequest && !originalRequest._retry) {
                    const targetUrl = (originalRequest.url || '').toLowerCase();
                    const isAuthEndpoint = targetUrl.includes('/auth/refresh') || targetUrl.includes('/auth/logout') || targetUrl.includes('login/simaka/fo') || targetUrl.includes('/user/login');

                    // Jika endpoint auth itu sendiri yang 401, jangan refresh lagi
                    if (isAuthEndpoint) {
                        clearAllAuthStorage();
                        window.location.replace('/loadingout');
                        return Promise.reject(error);
                    }

                    const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
                    if (!refreshToken) {
                        clearAllAuthStorage();
                        window.location.replace('/loadingout');
                        return Promise.reject(error);
                    }

                    originalRequest._retry = true;

                    try {
                        const newAccessToken = await getOrRefreshAccessToken('front-office');
                        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
                        return axiosInstance(originalRequest);
                    } catch (refreshError) {
                        clearAllAuthStorage();
                        window.location.replace('/loadingout');
                        return Promise.reject(refreshError);
                    }
                } else if (status === 403) {
                    const msg = (error.response.data && error.response.data.message)
                        ? error.response.data.message
                        : 'Akses Ditolak: Anda tidak memiliki izin untuk melakukan aksi ini.';
                    alert(msg);
                }
            }
            return Promise.reject(error);
        }
    );

    return axiosInstance;
}

// 1. Otomatis pasangkan ke default axios instance
attachJwtInterceptor(axios);

// 2. Monkey-patch axios.create agar setiap instance baru otomatis terpasang interceptor
if (typeof axios.create === 'function' && !axios.__createPatched) {
    axios.__createPatched = true;
    const originalCreate = axios.create.bind(axios);
    axios.create = function (...args) {
        const instance = originalCreate(...args);
        attachJwtInterceptor(instance);
        return instance;
    };
}

// 3. Inisialisasi token awal ke default axios headers
const bootToken = getAuthToken();
if (bootToken && axios && axios.defaults && axios.defaults.headers) {
    axios.defaults.headers.common = axios.defaults.headers.common || {};
    axios.defaults.headers.common['Authorization'] = `Bearer ${bootToken}`;
}

// 4. Intercept Native window.fetch API jika ada request yang menggunakan fetch langsung
if (typeof window !== 'undefined' && typeof window.fetch === 'function' && !window.__jwtFetchPatched) {
    window.__jwtFetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = async function (resource, init) {
        try {
            const urlString = typeof resource === 'string' ? resource : (resource && resource.url ? resource.url : '');
            const isAuthEndpoint = typeof urlString === 'string' && (
                urlString.includes('/auth/refresh') ||
                urlString.includes('/auth/logout') ||
                urlString.includes('signin') ||
                urlString.includes('login')
            );

            let token = getAuthToken();
            const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

            if (!isAuthEndpoint && refreshToken && (!token || isTokenExpired(token, 60))) {
                try {
                    token = await getOrRefreshAccessToken('front-office');
                } catch (e) {}
            }

            if (token && isInternalUrl(urlString) && !isAuthEndpoint) {
                init = init || {};
                let headers = init.headers;
                if (!headers) {
                    headers = {};
                    init.headers = headers;
                }
                const tokenType = localStorage.getItem('token_type') || sessionStorage.getItem('token_type') || 'Bearer';
                const authValue = token.startsWith('Bearer ') ? token : `${tokenType} ${token}`;

                if (headers instanceof Headers) {
                    if (!headers.has('Authorization')) {
                        headers.set('Authorization', authValue);
                    }
                } else if (Array.isArray(headers)) {
                    const hasAuth = headers.some(([key]) => key.toLowerCase() === 'authorization');
                    if (!hasAuth) {
                        headers.push(['Authorization', authValue]);
                    }
                } else if (typeof headers === 'object') {
                    if (!headers['Authorization'] && !headers['authorization']) {
                        headers['Authorization'] = authValue;
                    }
                }
            }
        } catch (e) {
            console.warn('Error in fetch JWT interceptor:', e);
        }
        return originalFetch(resource, init);
    };
}

// 5. Auto-Refresh on Tab Focus / Visibility Change (Saat user kembali ke tab setelah lama ditinggal)
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__jwtTabFocusListenerAttached_fo) {
    window.__jwtTabFocusListenerAttached_fo = true;
    const handleTabReactivation = () => {
        const token = getAuthToken();
        const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');
        const expired = !token || isTokenExpired(token, 60);
        if (refreshToken && expired) {
            console.log('[JWT FO] Tab reactivated & access token expired, triggering getOrRefreshAccessToken...');
            getOrRefreshAccessToken('front-office').catch((e) => {
                console.warn('[Auto-Refresh on Tab Focus Failed (FO)]:', e);
            });
        }
    };
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden || document.visibilityState === 'visible') {
            handleTabReactivation();
        }
    });
    window.addEventListener('focus', handleTabReactivation);
}

export default attachJwtInterceptor;
